/**
 * Full-track map gradient derived from the SCORE's own statistic (spec
 * 2026-08-20): each SAMPLE_INTERVAL bin is colored by the share of laps
 * within ±SPEED_OK_THRESHOLD_KMH of the bin median — so a red stretch on
 * the map IS the thing lowering a zone's curve %. Inside a zone window the
 * lap set is that zone's clean laps (map and curve % agree by
 * construction); elsewhere all included laps (straights stay visible but
 * unscored). Absolute normalization — a fully consistent track is fully
 * green, unlike the zone gradient's relative "every zone has a reddest
 * spot".
 *
 * Honesty rules (owner ruling): the gradient must never paint a stretch it
 * can't honestly score.
 *   1. A bin inside a zone window whose zone is `traffic-disrupted` emits NO
 *      point — a visible gap on the map, matching that zone card's "too
 *      much traffic to score" message, instead of implying a color judgment
 *      that was never made.
 *   2. A bin (anywhere) with fewer than MIN_LAPS_FOR_CONSISTENCY usable
 *      values also emits NO point, rather than defaulting to
 *      normalizedValue = 0 (green) — low confidence must never read as
 *      "consistent".
 */
import { type SpeedTrace } from './speedTraces'
import { binUsableLapValues } from './binSpeedSpreads'
import { median } from './robustStats'
import {
  ensureBinGpsFromTraces,
  type VarianceBin,
} from '../varianceBinProcessing'
import { type ZoneVariancePoint } from './consistencyGradient'
import { SAMPLE_INTERVAL } from '../../../config/chartDisplay'
import {
  SPEED_OK_THRESHOLD_KMH,
  MIN_LAPS_FOR_CONSISTENCY,
} from '../../../config/consistencyScoring'

export interface TrackGradientZoneWindow {
  startDistance: number
  endDistance: number
  setAsideLapIds: Set<string>
  /** True when this window's zone is `traffic-disrupted` — too much traffic
   *  to score. Bins inside get NO gradient point (a gap), never a color. */
  disrupted?: boolean
}

export function buildTrackConsistencyGradient(args: {
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  zoneWindows: TrackGradientZoneWindow[]
}): ZoneVariancePoint[] | null {
  const included = args.traces.filter((t) => !args.excludedLapIds.has(t.id))
  if (included.length < MIN_LAPS_FOR_CONSISTENCY) return null

  let maxDistance = 0
  for (const t of included) {
    const last = t.points[t.points.length - 1]
    if (last && last.distance > maxDistance) maxDistance = last.distance
  }
  const numBins = Math.floor(maxDistance / SAMPLE_INTERVAL)
  if (numBins <= 0) return null

  const binValues = binUsableLapValues(included, 0, numBins)

  const bins: VarianceBin[] = Array.from({ length: numBins }, (_, i) => ({
    distance: i * SAMPLE_INTERVAL,
    lat: 0,
    lng: 0,
  }))
  ensureBinGpsFromTraces(bins, included, 0)

  const points: ZoneVariancePoint[] = []
  for (let i = 0; i < numBins; i++) {
    const bin = bins[i]
    if (bin.lat === 0 && bin.lng === 0) continue
    const window = args.zoneWindows.find(
      (w) => bin.distance >= w.startDistance && bin.distance < w.endDistance
    )
    // Rule 1: a disrupted zone's window can't be honestly scored — leave a
    // gap rather than painting it.
    if (window?.disrupted) continue
    const values = window
      ? binValues[i].filter(
          (v) => !window.setAsideLapIds.has(included[v.lapIdx].id)
        )
      : binValues[i]
    // Rule 2: too few usable laps to score this bin honestly — skip it
    // (a gap) instead of defaulting to normalizedValue = 0 (green).
    if (values.length < MIN_LAPS_FOR_CONSISTENCY) continue
    const med = median(values.map((v) => v.value))
    const within = values.filter(
      (v) => Math.abs(v.value - med) <= SPEED_OK_THRESHOLD_KMH
    ).length
    const normalizedValue = 1 - within / values.length
    points.push({
      lat: bin.lat,
      lng: bin.lng,
      distance: bin.distance,
      normalizedValue,
    })
  }
  return points.length > 0 ? points : null
}
